import SearchBox from "@/common/SearchBox";
import { LayoutHeader } from "@/Header";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import {
  useTicketIssueList,
  useTicketIssueSummary,
  type TicketIssueListItem,
} from "~/services/business/myPage";

export const Route = createFileRoute(
  "/business/layout/user-subscription-issue-history",
)({
  component: SubscriptionIssueHistory,
  // 홈 '만료 예정 이용권' 바로가기에서 ?status=EXPIRING 으로 진입
  validateSearch: (search: Record<string, unknown>): { status?: string } => ({
    status: search.status != null ? String(search.status) : undefined,
  }),
});

type StatusFilter =
  | "ISSUE"
  | "REVOKE"
  | "USE"
  | "EXPIRED"
  | "EXPIRING"
  | null;

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: null, label: "전체" },
  { value: "ISSUE", label: "발급" },
  { value: "REVOKE", label: "회수" },
  { value: "USE", label: "사용" },
  { value: "EXPIRED", label: "만료" },
  { value: "EXPIRING", label: "만료예정" },
];

function SubscriptionIssueHistory() {
  const { history } = useRouter();
  const { status: initialStatus } = Route.useSearch();
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [currentSearchKeyword, setCurrentSearchKeyword] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(
    STATUS_OPTIONS.some((o) => o.value === initialStatus)
      ? (initialStatus as StatusFilter)
      : null,
  );

  // 발급 현황 요약 — 조회 월(YYYYMM), 기본은 이번 달
  const now = new Date();
  const [yearMonth, setYearMonth] = useState<string>(
    `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}`,
  );
  const { data: summary } = useTicketIssueSummary(yearMonth);
  const shiftMonth = (delta: number) => {
    const y = Number(yearMonth.slice(0, 4));
    const m = Number(yearMonth.slice(4, 6));
    const d = new Date(y, m - 1 + delta, 1);
    setYearMonth(
      `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}`,
    );
  };
  const ymLabel = `${yearMonth.slice(0, 4)}.${yearMonth.slice(4, 6)}`;
  const won = (n?: number) => (n ?? 0).toLocaleString();

  // 화면 진입 시 userName null, 돋보기 클릭 시 입력한 검색어 전달
  const {
    data: listData,
    isLoading,
    isFetching,
  } = useTicketIssueList({
    userName: currentSearchKeyword.trim() || null,
    status: statusFilter ?? undefined,
  });

  const handleSearch = (keyword: string) => {
    setCurrentSearchKeyword(keyword);
  };

  const list = listData?.data ?? [];

  /** 날짜 2줄 표시: 1줄=년, 2줄=월.일 */
  const formatIssueDate = (dt: string) => {
    if (!dt) return "-";
    const d = new Date(dt);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}\n${month}.${day}`;
  };

  /**
   * 유효기간 셀 표시.
   * 최초 이용일 방식 이용권은 첫 이용 전이면 임시 만료일(발급+1년) 대신 안내 문구를,
   * 시작 후에는 실제 만료일을 표시한다.
   */
  const formatValidity = (row: TicketIssueListItem) => {
    if (row.usageDurationDays != null) {
      if (!row.dtFirstUse) {
        return `첫 이용 시\n${row.usageDurationDays}일`;
      }
      return formatIssueDate(row.actualDtExpired || row.dtExpired);
    }
    return formatIssueDate(row.dtExpired);
  };

  /** 발급상태 코드 → 표시 텍스트 */
  const getStatusLabel = (code: string) => {
    switch (code) {
      case "ISSUE":
        return "발급";
      case "REVOKE":
        return "회수";
      case "USE":
        return "사용";
      case "EXPIRED":
        return "만료";
      case "CANCEL":
        return "취소";
      default:
        return code || "-";
    }
  };

  return (
    <>
      <LayoutHeader title="이용권 발급 내역" hasBackBtn hasFootPrint={false} />
      <div className="flex-1 min-h-0 overflow-y-auto bg-[#F5F5F5]">
        <div className="mx-2">
          <div className="flex justify-end items-center px-2 py-3">
            <SearchBox
              searchText={searchKeyword}
              setSearchText={setSearchKeyword}
              onSearch={handleSearch}
              placeholder="반려동물 또는 보호자 이름으로 검색"
            />
          </div>
        </div>
        {/* 발급 현황 요약 */}
        <div className="mx-4 mb-3 rounded-2xl bg-white p-3">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                type="button"
                aria-label="이전 달"
                onClick={() => shiftMonth(-1)}
                className="px-1 text-[#999]"
              >
                ‹
              </button>
              <span className="text-[15px] font-semibold">{ymLabel}</span>
              <button
                type="button"
                aria-label="다음 달"
                onClick={() => shiftMonth(1)}
                className="px-1 text-[#999]"
              >
                ›
              </button>
            </div>
            <span className="text-[12px] text-[#999]">이번 달 발급 현황</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-xl bg-[#FAEEDA] px-3 py-2.5">
              <div className="text-[12px] text-[#854F0B]">발급 건수</div>
              <div className="text-[22px] font-semibold text-[#633806]">
                {summary?.issueCount ?? 0}
                <span className="text-[13px] font-normal"> 건</span>
              </div>
            </div>
            <div className="rounded-xl bg-[#FAEEDA] px-3 py-2.5">
              <div className="text-[12px] text-[#854F0B]">발급액</div>
              <div className="text-[20px] font-semibold text-[#633806]">
                {won(summary?.issueAmount)}
                <span className="text-[13px] font-normal"> 원</span>
              </div>
            </div>
            <div className="rounded-xl bg-[#E1F5EE] px-3 py-2.5">
              <div className="text-[12px] text-[#0F6E56]">활성 이용권</div>
              <div className="text-[22px] font-semibold text-[#085041]">
                {summary?.activeCount ?? 0}
                <span className="text-[13px] font-normal"> 개</span>
              </div>
            </div>
            <div className="rounded-xl bg-[#EAF3DE] px-3 py-2.5">
              <div className="text-[12px] text-[#3B6D11]">발급 고객수</div>
              <div className="text-[22px] font-semibold text-[#27500A]">
                {summary?.customerCount ?? 0}
                <span className="text-[13px] font-normal"> 명</span>
              </div>
            </div>
          </div>
          <div className="mt-2 flex gap-1.5">
            {[
              { label: "발급", value: summary?.statusIssue },
              { label: "사용", value: summary?.statusUse },
              { label: "회수", value: summary?.statusRevoke },
              { label: "만료", value: summary?.statusExpired },
            ].map((s) => (
              <div
                key={s.label}
                className="flex-1 rounded-lg bg-[#F5F5F5] py-1.5 text-center"
              >
                <div className="text-[15px] font-semibold">{s.value ?? 0}</div>
                <div className="text-[11px] text-[#666]">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* 상태 필터 */}
        <div className="flex overflow-x-auto gap-1 px-4 mb-3">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.label}
              type="button"
              onClick={() => setStatusFilter(opt.value)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-[13px] font-medium transition-colors ${
                statusFilter === opt.value
                  ? "bg-[#333] text-white"
                  : "bg-white text-[#666] border border-[#e9e9e9] hover:bg-gray-50"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-center pl-1 mx-4 mb-2">
          <div className="text-[14px] font-semibold">총 {list.length}건</div>
        </div>
        {/* 컬럼 헤더: 이름 / 이용권 / 발급일 / 유효기간 / 발급상태 - 12칸 전부 사용 */}
        <div className="grid grid-cols-12 gap-1 items-center px-2 mx-4 mb-2">
          <div className="col-span-2 text-[13px] text-[#666666] text-center">
            이름
          </div>
          <div className="col-span-4 text-[13px] text-[#666666] text-center">
            이용권
          </div>
          <div className="col-span-2 text-[13px] text-[#666666] text-center">
            발급일
          </div>
          <div className="col-span-2 text-[13px] text-[#666666] text-center">
            유효기간
          </div>
          <div className="col-span-2 text-[13px] text-[#666666] text-center">
            발급상태
          </div>
        </div>
        {isLoading || isFetching ? (
          <div className="flex justify-center items-center py-16 text-[#666666]">
            로딩 중...
          </div>
        ) : list.length === 0 ? (
          <div className="flex justify-center items-center py-16 text-[#666666]">
            {currentSearchKeyword.trim()
              ? "검색 결과가 없습니다."
              : "발급 내역이 없습니다."}
          </div>
        ) : (
          list.map((row) => (
            <div key={row.purchaseTicketSeq} className="mx-4 mb-2">
              <button
                type="button"
                className="grid grid-cols-12 gap-1 items-center w-full bg-white rounded-lg border border-[#e9e9e9] min-h-[50px] px-2 py-3 cursor-pointer hover:bg-gray-50 text-left"
                onClick={() => {
                  const path = `/business/layout/user-subscription-issue-detail?purchaseTicketSeq=${row.purchaseTicketSeq}`;
                  (
                    history as { push: (path: string, state?: unknown) => void }
                  ).push(path, { row });
                }}
              >
                <div className="col-span-2 text-[13px] text-[#333] text-center whitespace-pre-line line-clamp-2">
                  {row.userName ?? "-"}
                </div>
                <div className="col-span-4 text-[13px] text-[#333] text-center truncate">
                  {row.ticketName ?? "-"}
                </div>
                <div className="col-span-2 text-[13px] text-[#333] text-center whitespace-pre-line">
                  {formatIssueDate(row.dtRegist)}
                </div>
                <div className="col-span-2 text-[13px] text-[#333] text-center whitespace-pre-line">
                  {formatValidity(row)}
                </div>
                <div className="col-span-2 text-[12px] leading-tight text-center">
                  <div
                    className={
                      row.status === "ISSUE"
                        ? "text-green-600 font-medium"
                        : "text-gray-500"
                    }
                  >
                    {getStatusLabel(row.status)}
                  </div>
                  {row.cdProductType !== "PRODUCT_TYPE_HOTEL" ? (
                    <>
                      <div className="text-gray-600">
                        {(row.qty ?? 0) - (row.remainQty ?? 0)}회 사용
                      </div>
                      <div
                        className={
                          (row.remainQty ?? 0) === 0
                            ? "text-gray-400"
                            : "text-orange-500 font-medium"
                        }
                      >
                        ({row.remainQty ?? 0}회 남음)
                      </div>
                    </>
                  ) : (
                    <div className="text-gray-400">-</div>
                  )}
                </div>
              </button>
            </div>
          ))
        )}
      </div>
    </>
  );
}
